import React, { Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import LoginPage from "../pages/login/login";
import LoginSmsCode from "../pages/login/loginSmsCode";
import ProtectedRoute from "../components/protectedRoute/ProtectedRoute";
import MainContentLayout from "../components/mainContentLayout/mainContentLayout";
import { Category } from "../types/types";
import UsersPage from "../pages/users/usersPage";
import HomePage from "../pages/home/home";
import ProtocolsMenuPage from "../pages/protocolsMenu/protocolsMenuPage";
import QuestionnairesPage from "../pages/questionnaires/questionnaires";
import NotFoundPage from "../pages/notFound/notFoundPage";
import MyAccount from "../components/myAccount/myAccount";
import UsersCategoriesPage from "../pages/usersCategories/usersCategoriesPage";
import SmsPage from "../pages/sms/sms";
import TreatmentGroupsPage from "../pages/treatmentGroups/treatmentGroups";
import UpdatesPage from "../pages/updates/updates";
import PublicOnlyRoute from "../components/publicOnlyRoute/PublicOnlyRoute";
import SsoRedirectHandler from "../components/ssoRedirectHandler/ssoRedirectHandler";
import { EditText } from "../components/EditText/EditText";
import CompanyOnboarding from "../pages/onboarding/companyOnboarding";
import OnboardingSuccess from "../pages/onboarding/onboardingSuccess";
import EditUserDataPage from "../pages/editUserData/editUserDataPage";
import ActionReportPage from "../pages/actionReport/actionReportPage";
import AppointmentsPage from "../pages/appointments/appointmentsPage";
import DailyView from "../pages/appointments/components/DailyView/DailyView";
import WeeklyView from "../pages/appointments/components/WeeklyView/WeeklyView";
import MonthlyView from "../pages/appointments/components/MonthlyView/MonthlyView";
import NoShowPredictor from "../pages/appointments/components/NoShowPredictor/NoShowPredictor";
import { ConflictsDetector } from "../pages/appointments/components/ConflictsDetector";
import TherapistsAvailability from "../pages/appointments/components/TherapistsAvailability/TherapistsAvailability";
import CalendarRedirect from "../pages/appointments/components/CalendarRedirect/CalendarRedirect";
import { useAppSelector } from "../services/hooks";
import { isSuperAdminByRoleId } from "../services/selectors/admin.selectors";
import DashboardLayout from "../components/dashboards/dashboardLayout/dashboardLayout";
import Loader from "../components/loader/loader";
import Management from "../components/management/management";

export const useRoutes = (
  changeCategory: (
    e: React.MouseEvent<Element, MouseEvent>,
    cat: Category
  ) => void,
  fakeCompanyId: number | null,
  dataL: boolean
) => {
  const isSuperAdmin = !!useAppSelector(isSuperAdminByRoleId);
  const QuestionnaireMapViewPage = React.lazy(
    () => import("../pages/questionnaireMapViewPage/questionnaireMapViewPage")
  );
  const EditQuestionnairePage = React.lazy(() => import("../pages/editQuestionnaire/editQuestionnaire"));
  const RrmMainPage = React.lazy(() => import("../pages/rrmMain/rrmMainPage"));
  const FeaturesPage = React.lazy(() => import("../pages/features/features"));
  const AddNewFeaturePage = React.lazy(() => import("../pages/addOrEditFeature/addNewFeature"));
  const EditFeaturePage = React.lazy(() => import("../pages/addOrEditFeature/editFeature"));
  const AddOrEditQuestion = React.lazy(() => import("../pages/addOrEditQuestion/addOrEditQuestion"));
  const AddOrEditOptions = React.lazy(() => import("../pages/addOrEditOptions/addOrEditOptions"));
  const RrmQuestionsPage = React.lazy(() => import("../pages/rrmQuestions/rrmQuestionsPage"));
  const ProtocolsPage = React.lazy(() => import("../pages/protocols/protocols"));
  const TreatmentProtocolsMenuPage = React.lazy(
    () => import("../pages/treatmentProtocols/treatmentProtocolsMenuPage"),
  );
  const TreatmentProtocolsPage = React.lazy(
    () => import("../pages/treatmentProtocols/treatmentProtocolsPage"),
  );
  const RrmGeneralPage = React.lazy(() => import("../pages/rrmMain/rrmGeneralPage"));
  const SupportPage = React.lazy(() => import("../pages/support/supportPage"));
  const ProfilePage = React.lazy(() => import("../pages/profile/profile"));
  const PlansPage = React.lazy(() => import("../pages/plans/plans"));
  const PricesPage = React.lazy(() => import("../pages/prices/prices"));
  const ComponentsPage = React.lazy(() => import("../pages/components/components"));
  const Dashboard = React.lazy(() => import("../components/dashboards/dashboard/dashboard"));
  const TherapistDashboard = React.lazy(() => import("../components/dashboards/therapistDashboard/therapistDashboard"));
  const ClientDashboard = React.lazy(() => import("../components/dashboards/clientDashboard/clientDashboard"));
  const CompaniesPage = React.lazy(() => import("../pages/companies/companiesPage"));
  const SupportMessagesPage = React.lazy(() => import("../pages/supportMessages/supportMessagesPage"));
  const LibraryPage = React.lazy(() => import("../pages/library/library"));
  const VenchiMarketplacePage = React.lazy(() => import("../pages/venchiMarketplace/venchiMarketplacePage"));
  const ChatPage = React.lazy(() => import("../pages/chat/chatPage"));
  const Faq = React.lazy(() => import("../components/faq/faq"));
  const PendingTreatmentPlans = React.lazy(() => import("../components/pendingTreatmentPlans/pendingTreatmentPlans"));
  const SpecializationsPage = React.lazy(() => import("../pages/specializations/specializations"));
  const VideoDiagnosticsPage = React.lazy(() => import("../pages/videoDiagnostics/videoDiagnosticsPage"));
  const ManageLanguages = React.lazy(() => import("../components/manageLanguages/manageLanguages"));
  const DynamicReportsPage = React.lazy(() => import("../pages/dynamicReports/dynamicReportsPage"));
  const SmartReportsPage = React.lazy(() => import("../pages/smartReports/smartReportsPage"));
  const ReportsPage = React.lazy(() => import("../pages/reports/reportsPage"));
  const PeriodicReportsPage = React.lazy(() => import("../pages/reports/periodicReportsPage"));
  const TherapistsReportsPage = React.lazy(() => import("../pages/reports/therapistsReportsPage"));
  const SmartReportHistoryPage = React.lazy(() => import("../pages/smartReports/history/smartReportHistoryPage"));
  const SmartReportViewPage = React.lazy(() => import("../pages/smartReports/history/smartReportViewPage"));
  const TutorialManagementPage = React.lazy(() => import("../pages/tutorialManagement/tutorialManagement"));
  const TablePermissionsPage = React.lazy(() => import("../pages/tablePermissions/tablePermissions"));
  const SmsQuestionnaireNotificationsPage = React.lazy(() => import("../pages/smsQuestionnaireNotifications/smsQuestionnaireNotifications"));
  const HotlineQuestionnairePage = React.lazy(() => import("../pages/hotlineQuestionnaire/hotlineQuestionnairePage"));
  const DailyDashboard = React.lazy(() => import("../components/dashboards/dailyDashboard/dailyDashboard"));
  const FinancialDashboard = React.lazy(() => import("../components/dashboards/financialDashboard/financialDashboard"));
  const ClinicalDashboard = React.lazy(() => import("../components/dashboards/clinicalDashboard/clinicalDashboard"));
  const BurnoutDashboard = React.lazy(() => import("../components/dashboards/burnoutDashboard/burnoutDashboard"));
  const TherapistsDashboard = React.lazy(() => import("../components/dashboards/therapistsDashboard/therapistsDashboard"));
  const PatientsDashboard = React.lazy(() => import("../components/dashboards/patientsDashboard/patientsDashboard"));
  const SalesDashboard = React.lazy(() => import("../components/dashboards/salesDashboard/salesDashboard"));
  const QuestionnaireDashboard = React.lazy(() => import("../components/dashboards/dashboard/dashboard"));
  const MeetingRecordingConfig = React.lazy(() => import("../pages/meetingRecordingConfig/meetingRecordingConfig"));
  const CompanySettings = React.lazy(() => import("../pages/companyDefaultSettings/CompanyDefaultSettingsPage"));
  const CrisisHotlineDashboard = React.lazy(() => import("../components/dashboards/crisisHotlineDashboard/crisisHotlineDashboard"));
  const ExternalServicesPage = React.lazy(() => import("../pages/externalServices/externalServicesPage"));
  const CompanyAiUsagePage = React.lazy(() => import("../pages/companyAiUsage/companyAiUsagePage"));
  const JobsListPage = React.lazy(() => import("../pages/jobs/jobsListPage"));
  const JobDetailPage = React.lazy(() => import("../pages/jobs/jobDetailPage"));
  const AuditLogPage = React.lazy(() => import("../pages/auditLog/auditLogPage"));
  const PendingReportsPage = React.lazy(() => import("../pages/reports/pendingReports"));
  const TreatmentRoomsPage = React.lazy(() => import("../pages/treatmentRooms/treatmentRooms"));
  const DevicesPage = React.lazy(() => import("../pages/devices/devicesPage"));

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {/*navigation from therapists site:*/}
        <Route path="/auth/sso" element={<SsoRedirectHandler />} />
        <Route
          path="/login/:guid?"
          element={
            <PublicOnlyRoute>
              <LoginPage
                setData={(setDataFromL) => {
                  return dataL;
                }}
              />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="login/enter_code"
          element={
            <PublicOnlyRoute>
              <LoginSmsCode />
            </PublicOnlyRoute>
          }
        />
        <Route
          path={"/"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Main}
                changeCategory={changeCategory}
              >
                <HomePage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/users/therapists"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Users}
                changeCategory={changeCategory}
              >
                <UsersPage usersCategory={"therapists"} />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/profile"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Profile}
                changeCategory={changeCategory}
              >
                <ProfilePage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/support"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Support}
                changeCategory={changeCategory}
              >
                <SupportPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/protocols/questionnaires/map-test/:id"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <QuestionnaireMapViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={"/smsDelivery"}
          element={<Navigate to="/companySettings?section=notifications" replace />}
        />
        <Route
          path={"/jobs"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Management}
                changeCategory={changeCategory}
              >
                <JobsListPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/jobs/:jobId"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Management}
                changeCategory={changeCategory}
              >
                <JobDetailPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={"/management"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Management}
                changeCategory={changeCategory}
              >
                <Management />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/audit-log"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Management}
                changeCategory={changeCategory}
              >
                <AuditLogPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        {fakeCompanyId == null && (
          <>
            <Route
              path={"/specializations"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <SpecializationsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/faq"
              element={
                <ProtectedRoute redirectPath="/login">
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <Faq />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/myAccount"
              element={
                <ProtectedRoute redirectPath="/login">
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <MyAccount />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/companySettings"
              element={
                <ProtectedRoute redirectPath="/login">
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <CompanySettings />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <ProtocolsMenuPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/protocols"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <ProtocolsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/treatment"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <TreatmentProtocolsMenuPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/treatment/:kind"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <TreatmentProtocolsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/components"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <ComponentsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/hotline"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <HotlineQuestionnairePage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/sms"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <SmsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />

            {isSuperAdmin && (
              <Route
                path={"/plans"}
                element={
                  <ProtectedRoute redirectPath={`/login`}>
                    <MainContentLayout
                      activeCategory={Category.Management}
                      changeCategory={changeCategory}
                    >
                      <PlansPage />
                    </MainContentLayout>
                  </ProtectedRoute>
                }
              />
            )}

            <Route
              path={"/treatment_rooms"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <TreatmentRoomsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path={"/devices"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <DevicesPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path={"/treatment_groups"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <TreatmentGroupsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path={"/updates"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <UpdatesPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path={"/prices"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <PricesPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path={"/table_permissions"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <TablePermissionsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path={"/sms_fill_alerts"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Management}
                    changeCategory={changeCategory}
                  >
                    <SmsQuestionnaireNotificationsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path={"/protocols/questionnaires"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <QuestionnairesPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/questionnaires/edit/:id?/copy?"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <EditQuestionnairePage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/questionnaires/edit/:qid/copy?/options/:id"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <AddOrEditOptions />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/questionnaires/edit/:qid/copy?/rrmMain/edit/:id?"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <AddOrEditQuestion />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/questionnaires/edit/:qid/questions/edit/:id?"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <AddOrEditQuestion />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/questionnaires/edit/:qid/rrm"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <RrmQuestionsPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/features"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <FeaturesPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/features/add_new"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <AddNewFeaturePage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"/protocols/features/edit/:id"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <EditFeaturePage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"library"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Library}
                    changeCategory={changeCategory}
                  >
                    <LibraryPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            {isSuperAdmin && (
              <Route
                path={"venchiMarketplace"}
                element={
                  <ProtectedRoute redirectPath={`/login`}>
                    <MainContentLayout
                      activeCategory={Category.VenchiMarketplace}
                      changeCategory={changeCategory}
                    >
                      <VenchiMarketplacePage />
                    </MainContentLayout>
                  </ProtectedRoute>
                }
              />
            )}
            <Route
              path={"protocols/RRM"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <RrmMainPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={"protocols/generalRRM"}
              element={
                <ProtectedRoute redirectPath={`/login`}>
                  <MainContentLayout
                    activeCategory={Category.Protocols}
                    changeCategory={changeCategory}
                  >
                    <RrmGeneralPage />
                  </MainContentLayout>
                </ProtectedRoute>
              }
            />
          </>
        )}
        <Route
          path={"/users"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Users}
                changeCategory={changeCategory}
              >
                <UsersCategoriesPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/appointments"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Appointments}
                changeCategory={changeCategory}
              >
                <AppointmentsPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        >
          {/* Default redirect to stored calendar view */}
          <Route index element={<CalendarRedirect includeCalendarPath={true} />} />

          {/* Calendar tab with view sub-routes */}
          <Route path="calendar">
            <Route index element={<CalendarRedirect includeCalendarPath={false} />} />
            <Route path="daily" element={<DailyView />} />
            <Route path="weekly" element={<WeeklyView />} />
            <Route path="monthly" element={<MonthlyView />} />
          </Route>

          {/* Therapists Availability tab */}
          <Route path="therapists-availability" element={<TherapistsAvailability />} />

          {/* No-Show Predictor tab */}
          <Route path="no-show-predictor" element={<NoShowPredictor />} />

          {/* Conflicts Detector tab */}
          <Route path="conflicts-detector" element={<ConflictsDetector />} />
        </Route>
        <Route
          path={"/users/clients"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Users}
                changeCategory={changeCategory}
              >
                <UsersPage usersCategory={"clients"} />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/users/edit"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <EditUserDataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={"/action-report"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <ActionReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={"*"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Users}
                changeCategory={changeCategory}
              >
                <NotFoundPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/videoDiagnostics"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.videoDiagnostics}
                changeCategory={changeCategory}
              >
                <VideoDiagnosticsPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/dashboards"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Dashboards}
                changeCategory={changeCategory}
              >
                {/* <Dashboard /> */}
                <TherapistDashboard />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={"/reports"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Reports}
                changeCategory={changeCategory}
              >
                <ReportsPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/reports/dynamic-reports"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Reports}
                changeCategory={changeCategory}
              >
                <DynamicReportsPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/reports/periodic"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Reports}
                changeCategory={changeCategory}
              >
                <PeriodicReportsPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/reports/therapists-reports"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Reports}
                changeCategory={changeCategory}
              >
                <TherapistsReportsPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={"/smart-reports"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Reports}
                changeCategory={changeCategory}
              >
                <SmartReportsPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={"reports/pending-reports"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Reports}
                changeCategory={changeCategory}
              >
                <PendingReportsPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={"/smart-reports/history"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Reports}
                changeCategory={changeCategory}
              >
                <SmartReportHistoryPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={"/smart-reports/history/:id"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Reports}
                changeCategory={changeCategory}
              >
                <SmartReportViewPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={"/therapistDashboard"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Main}
                changeCategory={changeCategory}
              >
                {/* <TherapistDashboard /> */}
                <Dashboard />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={"/clientDashboard"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Main}
                changeCategory={changeCategory}
              >
                <ClientDashboard />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />

        {/* New Dashboard Routes - Keep existing sidebar */}
        <Route
          path={"/dashboards"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Dashboards}
                changeCategory={changeCategory}
              >
                <DashboardLayout useCustomHeader={true} />
              </MainContentLayout>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="daily" replace />} />
          <Route path="daily" element={<DailyDashboard />} />
          <Route path="financial" element={<FinancialDashboard />} />
          <Route path="clinical" element={<ClinicalDashboard />} />
          <Route path="burnout" element={<BurnoutDashboard />} />
          <Route path="therapists" element={<TherapistsDashboard />} />
          <Route path="patients" element={<PatientsDashboard />} />
          <Route path="sales" element={<SalesDashboard />} />
          <Route path="questionnaire" element={<QuestionnaireDashboard />} />
          <Route path="crisis-hotline" element={<CrisisHotlineDashboard />} />
        </Route>
        <Route
          path={"/pendingTreatmentPlans"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Main}
                changeCategory={changeCategory}
              >
                <PendingTreatmentPlans />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/companies"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Main}
                changeCategory={changeCategory}
              >
                <CompaniesPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/supportMessages"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Main}
                changeCategory={changeCategory}
              >
                <SupportMessagesPage />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={"/edit"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Main}
                changeCategory={changeCategory}
              >
                <EditText></EditText>
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        {isSuperAdmin && (
          <Route
            path={"/ai"}
            element={
              <ProtectedRoute redirectPath={`/login`}>
                <MainContentLayout
                  activeCategory={Category.Main}
                  changeCategory={changeCategory}
                >
                  <ChatPage />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path={"/language"}
          element={
            <ProtectedRoute redirectPath={`/login`}>
              <MainContentLayout
                activeCategory={Category.Management}
                changeCategory={changeCategory}
              >
                <ManageLanguages />
              </MainContentLayout>
            </ProtectedRoute>
          }
        />
        {isSuperAdmin && (
          <Route
            path={"/meeting-recording-config"}
            element={
              <ProtectedRoute redirectPath={`/login`}>
                <MainContentLayout
                  activeCategory={Category.Management}
                  changeCategory={changeCategory}
                >
                  <MeetingRecordingConfig />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
        )}
        {isSuperAdmin && (
          <Route
            path={"/external-services"}
            element={
              <ProtectedRoute redirectPath={`/login`}>
                <MainContentLayout
                  activeCategory={Category.Management}
                  changeCategory={changeCategory}
                >
                  <ExternalServicesPage />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
        )}
        {isSuperAdmin && (
          <Route
            path={"/company-ai-usage"}
            element={
              <ProtectedRoute redirectPath={`/login`}>
                <MainContentLayout
                  activeCategory={Category.Management}
                  changeCategory={changeCategory}
                >
                  <CompanyAiUsagePage />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path="/onboarding/:selectedPlanId?"
          element={<CompanyOnboarding />}
        />
        <Route
          path="/onboarding/success/:companyId?"
          element={<OnboardingSuccess />}
        />
        {isSuperAdmin && (
          <Route
            path={"/tutorialManagement"}
            element={
              <ProtectedRoute redirectPath={`/login`}>
                <MainContentLayout
                  activeCategory={Category.TutorialManagement}
                  changeCategory={changeCategory}
                >
                  <TutorialManagementPage />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
        )}
      </Routes>
    </Suspense>
  );
};
